import { addDays, startOfDay } from "date-fns";
import React from "react";
import { z } from "zod";

import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";

import {
	ButtonStandard,
	FormDatePicker,
	FormInputText,
	FormSubscribe,
	Icon,
	ModalBase,
	PYForm,
	SubmitButton,
	Text,
} from "@performyard/py-components";
import { CompensationCycleResponse } from "@performyard/service-contracts";

import { useCompanySettings } from "../../../../api/hooks/defs/company";
import { useUpdateCompensationCycleDetails } from "../../../../api/hooks/defs/compensation";

import { dateToTimeInTimeZone, utcToTimeInTimeZone } from "../../utils";

const editCycleSchema = z
	.object({
		name: z
			.string()
			.trim()
			.min(1, "This is a required field")
			.max(255, "Cycle name must be 255 characters or less"),
		startDate: z
			.date({ error: "This is a required field" })
			.nullable()
			.refine((val): val is Date => val !== null, "This is a required field"),
		dueDate: z
			.date({ error: "This is a required field" })
			.nullable()
			.refine((val): val is Date => val !== null, "This is a required field"),
	})
	.refine(
		(data) =>
			!data.startDate ||
			!data.dueDate ||
			data.dueDate.getTime() >= startOfDay(addDays(data.startDate, 1)).getTime(),
		{
			message: "Due date must be after the start date",
			path: ["dueDate"],
		}
	);

type EditCycleModalProps = {
	cycleId: string;
	closeModal: () => void;
	cycleDetails: CompensationCycleResponse;
};

const EditCycleModal: React.FC<EditCycleModalProps> = ({ cycleId, closeModal, cycleDetails }) => {
	const { data: companySettings } = useCompanySettings();

	const { mutate: updateCycleDetails } = useUpdateCompensationCycleDetails();
	const isStartDateInPast = new Date(cycleDetails.startDate) < new Date();
	const companyDefaultCycleStartTime = companySettings?.functions?.reviews?.["default_review_start_time"] ?? 12;
	const companyDefaultCycleDueTime = companySettings?.functions?.reviews?.["default_review_cycle_due_time"] ?? 12;
	return (
		<ModalBase isOpen onClose={closeModal} dangerouslySetClassName="!overflow-hidden w-[432px]">
			<div className="flex w-[382px] flex-col px-6 pb-6">
				<div className="mb-4 text-center">
					<Text size="sub-heading" weight="semi-bold">
						Edit Cycle Details
					</Text>
				</div>
				<PYForm
					id="edit-cycle-details"
					schema={editCycleSchema}
					defaultValues={{
						name: cycleDetails.name,
						startDate: utcToTimeInTimeZone(new Date(cycleDetails.startDate), cycleDetails.timezone),
						dueDate: utcToTimeInTimeZone(new Date(cycleDetails.dueDate), cycleDetails.timezone),
					}}
					onSubmit={async ({ value }) => {
						const timezone = cycleDetails.timezone;
						updateCycleDetails(
							{
								cycleId,
								data: {
									...value,
									startDate: dateToTimeInTimeZone(value.startDate, timezone),
									dueDate: dateToTimeInTimeZone(value.dueDate, timezone),
								},
							},
							{
								onSuccess: () => {
									closeModal();
								},
							}
						);
					}}
				>
					<div className="flex flex-col gap-4">
						<FormInputText id="name" label="Compensation Cycle Name" clearable />
						<FormDatePicker
							id="startDate"
							label="Start Date"
							size="medium"
							disabled={isStartDateInPast}
							showTimeDropdown={true}
							defaultTime={companyDefaultCycleStartTime}
						/>
						<FormDatePicker
							id="dueDate"
							label="Due Date"
							size="medium"
							showTimeDropdown={true}
							defaultTime={companyDefaultCycleDueTime}
						/>
						<div className="-mt-3 flex w-[384px]">
							<Icon icon={faInfoCircle} color="neutral-9" />
							<Text size="paragraph-3" weight="regular">
								All dates are set to your company timezone {cycleDetails.timezone}
							</Text>
						</div>
					</div>
					<FormSubscribe selector={(state) => [state?.isDirty, state?.isSubmitting]}>
						{([isDirty, isSubmitting]) => (
							<div className="mt-6 flex flex-row items-center justify-center gap-2">
								<div className="w-[90px]">
									<ButtonStandard variant="outline" color="neutral" size="large" onClick={closeModal}>
										Cancel
									</ButtonStandard>
								</div>
								<div className="w-[127px]">
									<SubmitButton
										variant="solid"
										color="primary"
										size="large"
										disabled={!isDirty || isSubmitting}
									>
										Save Changes
									</SubmitButton>
								</div>
							</div>
						)}
					</FormSubscribe>
				</PYForm>
			</div>
		</ModalBase>
	);
};

export default EditCycleModal;
